import React, { useState, useEffect, useMemo } from 'react';

// Color mapping by pillar/subject area for Mathematics
const mathPillarColors: Record<string, string> = {
  numbers_and_algebra: '#10b981', // emerald
  measurement_space: '#0ea5e9',   // blue
  statistics: '#f43f5e',          // rose
  calculus: '#8b5cf6',            // violet
  financial_mathematics: '#f59e0b', // amber
};

// Color mapping for English
const englishPillarColors: Record<string, string> = {
  speaking_listening: '#10b981',    // emerald
  reading_viewing: '#0ea5e9',       // blue
  writing_creating: '#8b5cf6',      // violet
  grammar_vocabulary: '#f59e0b',    // amber
  understanding_texts: '#f43f5e',   // rose
};

// Subject configuration
export type SubjectType = 'mathematics' | 'english';

interface SubjectConfig {
  dataFile: string;
  pillarColors: Record<string, string>;
  pillarField: string;
  pillarOrder: string[];
  displayName: string;
}

const subjectConfigs: Record<SubjectType, SubjectConfig> = {
  mathematics: {
    dataFile: '/curriculum-data.json',
    pillarColors: mathPillarColors,
    pillarField: 'math_study_pillar',
    pillarOrder: ['numbers_and_algebra', 'measurement_space', 'statistics', 'calculus', 'financial_mathematics'],
    displayName: 'Mathematics',
  },
  english: {
    dataFile: '/english-curriculum-data.json',
    pillarColors: englishPillarColors,
    pillarField: 'english_study_pillar',
    pillarOrder: ['speaking_listening', 'reading_viewing', 'writing_creating', 'grammar_vocabulary', 'understanding_texts'],
    displayName: 'English',
  },
};

// Raw data structure from the JSON
interface RawModuleData {
  year: number;
  math_study_pillar?: string;
  english_study_pillar?: string;
  module_id: string;
  module_generic_name: string;
  requires_1: string;
  requires_2: string;
  submodule: string;
  submodule_order: number;
}

// Processed module for display
interface ProcessedModule {
  id: string;
  name: string;
  color: string;
  pillar: string;
}

// Year row with modules
interface YearRow {
  year: string;
  modules: ProcessedModule[];
}

// Prerequisite link
interface PrerequisiteLink {
  from: string;
  to: string;
}

interface CurriculumMapProps {
  className?: string;
  subject?: SubjectType;
  onSubjectChange?: (subject: SubjectType) => void;
  showSubjectSelector?: boolean;
}

const CurriculumMap: React.FC<CurriculumMapProps> = ({
  className = '',
  subject = 'mathematics',
  onSubjectChange,
  showSubjectSelector = true,
}) => {
  const [rawData, setRawData] = useState<RawModuleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [currentSubject, setCurrentSubject] = useState<SubjectType>(subject);

  const config = subjectConfigs[currentSubject];

  // Load data from JSON file when subject changes
  useEffect(() => {
    setLoading(true);
    fetch(config.dataFile)
      .then(response => response.json())
      .then((data: RawModuleData[]) => {
        setRawData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading curriculum data:', error);
        setLoading(false);
      });
  }, [config.dataFile]);

  // Handle subject change
  const handleSubjectChange = (newSubject: SubjectType) => {
    setCurrentSubject(newSubject);
    onSubjectChange?.(newSubject);
  };

  // Process raw data into year rows and prerequisite links
  const { yearRows, prerequisiteLinks, nodePositions } = useMemo(() => {
    if (rawData.length === 0) {
      return { yearRows: [], prerequisiteLinks: [], nodePositions: {} };
    }

    // Group by year and get unique modules
    const yearModulesMap = new Map<number, Map<string, ProcessedModule>>();
    const allPrerequisites: PrerequisiteLink[] = [];
    const seenLinks = new Set<string>();

    rawData.forEach(item => {
      // Get or create year entry
      if (!yearModulesMap.has(item.year)) {
        yearModulesMap.set(item.year, new Map());
      }
      const yearModules = yearModulesMap.get(item.year)!;

      // Get pillar based on subject
      const pillar = currentSubject === 'mathematics'
        ? item.math_study_pillar
        : item.english_study_pillar;

      // Add module if not already present
      if (!yearModules.has(item.module_id)) {
        yearModules.set(item.module_id, {
          id: item.module_id,
          name: item.module_generic_name,
          color: config.pillarColors[pillar || ''] || '#94a3b8',
          pillar: pillar || '',
        });
      }

      // Add prerequisite links
      if (item.requires_1 && item.requires_1.trim() !== '') {
        const linkKey1 = `${item.requires_1}->${item.module_id}`;
        if (!seenLinks.has(linkKey1)) {
          allPrerequisites.push({ from: item.requires_1, to: item.module_id });
          seenLinks.add(linkKey1);
        }
      }
      if (item.requires_2 && item.requires_2.trim() !== '') {
        const linkKey2 = `${item.requires_2}->${item.module_id}`;
        if (!seenLinks.has(linkKey2)) {
          allPrerequisites.push({ from: item.requires_2, to: item.module_id });
          seenLinks.add(linkKey2);
        }
      }
    });

    // Sort years and create rows
    const sortedYears = Array.from(yearModulesMap.keys()).sort((a, b) => a - b);
    const rows: YearRow[] = sortedYears.map(year => {
      const modules = Array.from(yearModulesMap.get(year)!.values());
      // Sort modules by pillar for consistent ordering
      modules.sort((a, b) => {
        const aIdx = config.pillarOrder.indexOf(a.pillar);
        const bIdx = config.pillarOrder.indexOf(b.pillar);
        if (aIdx !== bIdx) return aIdx - bIdx;
        return a.name.localeCompare(b.name);
      });
      return {
        year: year === 0 ? 'K' : `Y${year}`,
        modules,
      };
    });

    // Layout constants
    const padding = 40;
    const yearLabelWidth = 45;
    const rowHeight = 70;
    const nodeGap = 110;
    const startY = 40;

    // Build node position map
    const positions: Record<string, { x: number; y: number; row: number; col: number }> = {};
    rows.forEach((row, rowIndex) => {
      row.modules.forEach((module, moduleIndex) => {
        positions[module.id] = {
          x: yearLabelWidth + padding + moduleIndex * nodeGap,
          y: startY + rowIndex * rowHeight,
          row: rowIndex,
          col: moduleIndex,
        };
      });
    });

    return {
      yearRows: rows,
      prerequisiteLinks: allPrerequisites,
      nodePositions: positions,
    };
  }, [rawData, currentSubject, config.pillarColors, config.pillarOrder]);

  // Layout constants
  const padding = 40;
  const yearLabelWidth = 45;
  const rowHeight = 70;
  const nodeRadius = 6;
  const nodeGap = 110;
  const startY = 40;

  // Calculate dimensions
  const maxNodes = yearRows.length > 0 ? Math.max(...yearRows.map(y => y.modules.length)) : 10;
  const width = yearLabelWidth + padding * 2 + maxNodes * nodeGap + 100;
  const height = startY + yearRows.length * rowHeight + padding;

  // Find connected nodes for highlighting
  const getConnectedNodes = (nodeId: string): Set<string> => {
    const connected = new Set<string>();
    prerequisiteLinks.forEach(link => {
      if (link.from === nodeId) connected.add(link.to);
      if (link.to === nodeId) connected.add(link.from);
    });
    return connected;
  };

  const connectedNodes = hoveredNode ? getConnectedNodes(hoveredNode) : new Set<string>();

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-slate-400">Loading {config.displayName} curriculum data...</div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Subject selector */}
      {showSubjectSelector && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-slate-400">Subject:</span>
          <div className="flex gap-1 p-1 rounded-lg bg-slate-800/50">
            {(Object.keys(subjectConfigs) as SubjectType[]).map((subj) => (
              <button
                key={subj}
                onClick={() => handleSubjectChange(subj)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  currentSubject === subj
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {subjectConfigs[subj].displayName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pillar legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {config.pillarOrder.map((pillar) => (
          <div key={pillar} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{
                background: config.pillarColors[pillar],
                boxShadow: `0 0 8px ${config.pillarColors[pillar]}80`,
              }}
            />
            <span className="text-xs text-slate-400 capitalize">
              {pillar.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>

      {/* SVG visualization */}
      <div className="overflow-x-auto">
        <svg
          width={width}
          height={height}
          style={{ minWidth: width, background: 'transparent' }}
        >
          <defs>
            <filter id="node-glow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="node-glow-strong" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <marker
              id="arrowhead"
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 6 3, 0 6" fill="#475569" />
            </marker>
            <marker
              id="arrowhead-highlight"
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 6 3, 0 6" fill="#0ea5e9" />
            </marker>
          </defs>

          {/* Prerequisite links */}
          {prerequisiteLinks.map((link, index) => {
            const fromPos = nodePositions[link.from];
            const toPos = nodePositions[link.to];
            if (!fromPos || !toPos) return null;

            const isHighlighted = hoveredNode === link.from || hoveredNode === link.to;

            // Calculate control points for curved path
            const dx = toPos.x - fromPos.x;
            const isSameColumn = Math.abs(dx) < 10;
            const isAdjacent = Math.abs(toPos.row - fromPos.row) === 1;

            // For adjacent rows in same column, use straight line
            if (isSameColumn && isAdjacent) {
              return (
                <line
                  key={`link-${index}`}
                  x1={fromPos.x}
                  y1={fromPos.y + nodeRadius + 2}
                  x2={toPos.x}
                  y2={toPos.y - nodeRadius - 6}
                  stroke={isHighlighted ? '#0ea5e9' : '#475569'}
                  strokeWidth={isHighlighted ? 2 : 1}
                  opacity={hoveredNode && !isHighlighted ? 0.15 : 0.5}
                  markerEnd={isHighlighted ? 'url(#arrowhead-highlight)' : 'url(#arrowhead)'}
                />
              );
            }

            // For cross-column or multi-row links, use curved path
            const controlOffset = Math.min(Math.abs(dx) * 0.5, 50);

            return (
              <path
                key={`link-${index}`}
                d={`M ${fromPos.x} ${fromPos.y + nodeRadius + 2}
                    C ${fromPos.x} ${fromPos.y + controlOffset + nodeRadius},
                      ${toPos.x} ${toPos.y - controlOffset - nodeRadius},
                      ${toPos.x} ${toPos.y - nodeRadius - 6}`}
                fill="none"
                stroke={isHighlighted ? '#0ea5e9' : '#475569'}
                strokeWidth={isHighlighted ? 2 : 1}
                opacity={hoveredNode && !isHighlighted ? 0.15 : 0.5}
                markerEnd={isHighlighted ? 'url(#arrowhead-highlight)' : 'url(#arrowhead)'}
              />
            );
          })}

          {/* Stage separators */}
          {[6, 10].map((afterYear) => {
            const rowIndex = yearRows.findIndex(r => r.year === `Y${afterYear}`);
            if (rowIndex === -1) return null;
            const y = startY + (rowIndex + 1) * rowHeight - rowHeight / 2;
            return (
              <g key={`separator-${afterYear}`}>
                <line
                  x1={padding}
                  y1={y}
                  x2={width - padding}
                  y2={y}
                  stroke="#334155"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  opacity="0.5"
                />
                <text
                  x={width - padding}
                  y={y - 5}
                  fill="#64748b"
                  fontSize="10"
                  fontFamily="system-ui, -apple-system, sans-serif"
                  textAnchor="end"
                >
                  {afterYear === 6 ? 'High School →' : 'Senior →'}
                </text>
              </g>
            );
          })}

          {/* Year labels and nodes */}
          {yearRows.map((row, rowIndex) => (
            <g key={row.year}>
              {/* Year label */}
              <text
                x={padding - 5}
                y={startY + rowIndex * rowHeight + 4}
                fill="#94a3b8"
                fontSize="12"
                fontWeight="700"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {row.year}
              </text>

              {/* Nodes */}
              {row.modules.map((module, moduleIndex) => {
                const cx = yearLabelWidth + padding + moduleIndex * nodeGap;
                const cy = startY + rowIndex * rowHeight;
                const isHovered = hoveredNode === module.id;
                const isConnected = connectedNodes.has(module.id);
                const isDimmed = hoveredNode && !isHovered && !isConnected;

                return (
                  <g
                    key={module.id}
                    onMouseEnter={() => setHoveredNode(module.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Node dot with glow */}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isHovered ? nodeRadius + 2 : nodeRadius}
                      fill={module.color}
                      filter={isHovered ? 'url(#node-glow-strong)' : 'url(#node-glow)'}
                      opacity={isDimmed ? 0.3 : 1}
                      style={{ transition: 'all 0.2s ease' }}
                    />
                    {/* Node label */}
                    <text
                      x={cx + nodeRadius + 8}
                      y={cy + 4}
                      fill={isDimmed ? '#475569' : '#e2e8f0'}
                      fontSize="10"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      opacity={isDimmed ? 0.4 : 1}
                      style={{ transition: 'all 0.2s ease' }}
                    >
                      {module.name.length > 18 ? module.name.substring(0, 16) + '...' : module.name}
                    </text>
                  </g>
                );
              })}
            </g>
          ))}

          {/* Stats display */}
          <g transform={`translate(${width - 150}, ${height - 50})`}>
            <text fill="#64748b" fontSize="10" fontFamily="system-ui">
              {yearRows.length} Year Levels
            </text>
            <text y="14" fill="#64748b" fontSize="10" fontFamily="system-ui">
              {Object.keys(nodePositions).length} Modules
            </text>
            <text y="28" fill="#64748b" fontSize="10" fontFamily="system-ui">
              {prerequisiteLinks.length} Prerequisites
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default CurriculumMap;
